import { useCountryInfo } from 'legacy/lut/hooks/useCountryInfo'

/* TODO: Replace with own hooks */
import { useCountrySource } from 'legacy/lut/hooks/useCountrySource'
import { useDropdownSource } from 'legacy/lut/hooks/useDropdownSource'
import { usePreferredLanguageSource } from 'legacy/lut/hooks/usePreferredLanguageSource'
import { memo, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { FormPhoneInput } from 'clickNgo/components/formPhoneInput'
import { Roles } from 'clickNgo/configs/roles'
import { useCheckSuperAdminAccess } from 'clickNgo/hooks/access'
import { useEmailPixelsScope } from 'clickNgo/hooks/validation/useEmailPixelsScope'
import { IAccessibility } from 'clickNgo/models/widget'
import {
  FormCheckBox,
  FormDropdown,
  FormMultiselect,
  useGetFormDataField,
} from 'cng-common/features/form'
import { FieldWrapperWithChanges } from 'cng-common/features/form/components'
import { ValidationType } from 'cng-common/features/form/configs'
import { wasFieldModified } from 'cng-common/features/form/helpers'
import { IForm } from 'core-components/classes/models/IForm'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'
import { FieldGroup } from 'shared/components/fieldGroup'

import { ContactFormContent } from '../../config/forms'
import { IContactFormUtils, TContactFormKeys } from '../../model/forms'
import {
  IContactCommonFormHandlers,
  IContactCommonFormRequiredFields,
  IContactCommonFormValidation,
} from '../../model/forms/common'
import { IContact } from '../../model/types'

import { getJobTitleSource, isHpEmail } from './helpers'
import { YesNoCheckboxes } from './YesNoCheckboxes'

import styles from './ContactCommonInfo.module.scss'

interface IProps
  extends IAccessibility,
    IContactFormUtils,
    IContactCommonFormHandlers,
    IContactCommonFormValidation,
    IContactCommonFormRequiredFields {
  contact: IContact
  formKeys: TContactFormKeys
  isCollapsible?: boolean
  isCollapsed?: boolean
  validationType?: ValidationType
}

export const ContactCommonInfo = memo<IProps>((props) => {
  const { roleIds } = useSelector(selectUser)
  const isSuperAdmin = useCheckSuperAdminAccess()

  const hasCommunicationPreferenceRole =
    roleIds.includes(Roles.COMMUNICATION_PREFERENCE) || isSuperAdmin

  const [countryOfBusinessFormValue] = useGetFormDataField<string>(
    props.formKeys.common,
    ContactFormContent.fields.countryOfBusiness.field
  )
  const [preferredLanguageValue] = useGetFormDataField<string>(
    props.formKeys.common,
    ContactFormContent.fields.preferredLanguage.field
  )
  const [, , isMobilePhoneNumberModified] = useGetFormDataField<string>(
    props.formKeys.common,
    ContactFormContent.fields.mobilePhoneNumber.field
  )
  const [mobilePhoneCode, updateMobilePhoneCode] = useGetFormDataField<string>(
    props.formKeys.common,
    ContactFormContent.fields.mobilePhoneCode.field
  )
  const [, , isWorkPhoneNumberModified] = useGetFormDataField<string>(
    props.formKeys.common,
    ContactFormContent.fields.workPhoneNumber.field
  )
  const [workPhoneCode, updateWorkPhoneCode] = useGetFormDataField<string>(
    props.formKeys.common,
    ContactFormContent.fields.workPhoneCode.field
  )
  const [emailFormValue] = useGetFormDataField<string>(
    props.formKeys.general,
    ContactFormContent.fields.email.field
  )

  const isEmailPixelsInScope = useEmailPixelsScope(countryOfBusinessFormValue) === 'in'
  const isEmailPixelsRequired =
    isEmailPixelsInScope && hasCommunicationPreferenceRole && !isHpEmail(emailFormValue)

  const countryInfo = useCountryInfo(countryOfBusinessFormValue)
  const countrySource = useCountrySource()

  const senioritySource = useDropdownSource({
    dropdownKey: 'Seniority',
  })

  const salutationSource = useDropdownSource({
    dropdownKey: 'Salutation',
  })

  const jobTitleSource = useMemo(() => getJobTitleSource(), [])

  const isPreferredLanguageDisabled = useMemo(() => {
    return !countryOfBusinessFormValue || props.isDisabled || props.isDisabledByRegion
  }, [countryOfBusinessFormValue, props.isDisabled, props.isDisabledByRegion])

  const preferredLanguageSource = usePreferredLanguageSource(
    countryInfo?.name,
    preferredLanguageValue
  )

  useEffect(() => {
    const isNewUpdatePhoneCode =
      !props.isEditMode && !mobilePhoneCode && !isMobilePhoneNumberModified

    if (isNewUpdatePhoneCode && countryInfo?.phoneCode) {
      updateMobilePhoneCode(countryInfo.phoneCode)
    }
  }, [
    countryInfo?.phoneCode,
    isMobilePhoneNumberModified,
    mobilePhoneCode,
    props.isEditMode,
    updateMobilePhoneCode,
  ])

  useEffect(() => {
    const isNewUpdatePhoneCode = !props.isEditMode && !workPhoneCode && !isWorkPhoneNumberModified

    if (isNewUpdatePhoneCode && countryInfo?.phoneCode) {
      updateWorkPhoneCode(countryInfo.phoneCode)
    }
  }, [
    countryInfo?.phoneCode,
    isWorkPhoneNumberModified,
    workPhoneCode,
    updateWorkPhoneCode,
    props.isEditMode,
  ])

  return (
    <>
      <FieldGroup
        title={ContactFormContent.titles.common}
        isCollapsible={props.isCollapsible}
        isCollapsed={props.isCollapsed}
      >
        <FieldWrapperWithChanges
          hasChanges={wasFieldModified(
            props.contact.modifiedValues,
            ContactFormContent.fields.salutation.field
          )}
          isHidden={!props.isReadOnly}
        >
          <FormDropdown
            source={salutationSource}
            caption="value"
            defaultValue={props.contact.salutation}
            field={ContactFormContent.fields.salutation.field}
            label={ContactFormContent.fields.salutation.label}
            formKey={props.formKeys.common}
            onChange={props.onSalutationChange}
            onValidate={props.onSalutationValidate}
            isRequired={props.isSalutationRequired}
            isStatic={true}
            isReadOnly={props.isReadOnly}
            validationType={props.validationType}
          />
        </FieldWrapperWithChanges>
        <FieldWrapperWithChanges
          hasChanges={wasFieldModified(
            props.contact.modifiedValues,
            ContactFormContent.fields.seniority.field
          )}
          isHidden={!props.isReadOnly}
        >
          <FormDropdown<IForm>
            source={senioritySource}
            caption="value"
            defaultValue={props.contact.seniority}
            field={ContactFormContent.fields.seniority.field}
            label={ContactFormContent.fields.seniority.label}
            formKey={props.formKeys.common}
            onChange={props.onSeniorityChange}
            onValidate={props.onSeniorityValidate}
            isRequired={props.isSeniorityRequired}
            isStatic={true}
            isReadOnly={props.isReadOnly}
            validationType={props.validationType}
          />
        </FieldWrapperWithChanges>
        <FieldWrapperWithChanges
          hasChanges={wasFieldModified(
            props.contact.modifiedValues,
            ContactFormContent.fields.primaryJobRole.field
          )}
          isHidden={!props.isReadOnly}
        >
          <FormMultiselect<string[]>
            defaultValue={props.contact.primaryJobRole}
            field={ContactFormContent.fields.primaryJobRole.field}
            label={ContactFormContent.fields.primaryJobRole.label}
            formKey={props.formKeys.common}
            source={jobTitleSource}
            caption="value"
            onChange={props.onPrimaryJobRoleChange}
            onValidate={props.onPrimaryJobRoleValidate}
            isRequired={props.isPrimaryJobRoleRequired}
            isDisabled={props.isDisabled || props.isDisabledByRegion}
            isReadOnly={props.isReadOnly}
            validationType={props.validationType}
            isStatic
          />
        </FieldWrapperWithChanges>
        <FieldWrapperWithChanges
          hasChanges={wasFieldModified(
            props.contact.modifiedValues,
            ContactFormContent.fields.countryOfBusiness.field
          )}
          isHidden={!props.isReadOnly}
        >
          <FormDropdown
            source={countrySource}
            defaultValue={props.contact.countryOfBusiness}
            field={ContactFormContent.fields.countryOfBusiness.field}
            label={ContactFormContent.fields.countryOfBusiness.label}
            formKey={props.formKeys.common}
            keyField="value"
            caption="name"
            onChange={props.onCountryOfBusinessChange}
            onValidate={props.onCountryOfBusinessValidate}
            validationType={props.validationType}
            isRequired={props.isCountryOfBusinessRequired}
            isDisabled={props.isDisabled || props.isDisabledByRegion}
            isReadOnly={props.isReadOnly}
          />
        </FieldWrapperWithChanges>
        <FieldWrapperWithChanges
          hasChanges={wasFieldModified(
            props.contact.modifiedValues,
            ContactFormContent.fields.preferredLanguage.field
          )}
          isHidden={!props.isReadOnly}
        >
          <FormDropdown
            source={preferredLanguageSource.source}
            defaultValue={props.contact.preferredLanguage}
            field={ContactFormContent.fields.preferredLanguage.field}
            label={ContactFormContent.fields.preferredLanguage.label}
            formKey={props.formKeys.common}
            caption="name"
            validationType={props.validationType}
            onChange={props.onPreferredLanguageChange}
            onValidate={props.onPreferredLanguageValidate}
            isRequired={
              props.isPrimaryJobRoleRequired ||
              (!isPreferredLanguageDisabled && preferredLanguageSource.isRequiredDataExist)
            }
            isDisabled={isPreferredLanguageDisabled}
            isReadOnly={props.isReadOnly}
          />
        </FieldWrapperWithChanges>
        <FieldWrapperWithChanges
          hasChanges={
            wasFieldModified(
              props.contact.modifiedValues,
              ContactFormContent.fields.mobilePhoneCode.field
            ) ||
            wasFieldModified(
              props.contact.modifiedValues,
              ContactFormContent.fields.mobilePhoneNumber.field
            )
          }
          isHidden={!props.isReadOnly}
        >
          <FormPhoneInput
            formKey={props.formKeys.common}
            label={ContactFormContent.fields.mobilePhoneNumber.label}
            phoneCodeValue={props.contact.mobilePhoneCode}
            phoneNumberValue={props.contact.mobilePhoneNumber}
            phoneCodeFieldName={ContactFormContent.fields.mobilePhoneCode.field}
            phoneNumberFieldName={ContactFormContent.fields.mobilePhoneNumber.field}
            onPhoneCodeChange={props.onMobilePhoneCodeChange}
            validationType={props.validationType}
            onPhoneNumberChange={props.onMobilePhoneNumberChange}
            onPhoneCodeValidate={props.onMobilePhoneCodeValidate}
            onPhoneNumberValidate={props.onMobilePhoneNumberValidate}
            isPhoneCodeRequired={props.isMobilePhoneCodeRequired}
            isPhoneNumberRequired={props.isMobilePhoneNumberRequired}
            isDisabled={props.isDisabled || props.isDisabledByRegion}
            isReadOnly={props.isReadOnly}
          />
        </FieldWrapperWithChanges>
        <FieldWrapperWithChanges
          hasChanges={
            wasFieldModified(
              props.contact.modifiedValues,
              ContactFormContent.fields.workPhoneCode.field
            ) ||
            wasFieldModified(
              props.contact.modifiedValues,
              ContactFormContent.fields.workPhoneNumber.field
            )
          }
          isHidden={!props.isReadOnly}
        >
          <FormPhoneInput
            formKey={props.formKeys.common}
            label={ContactFormContent.fields.workPhoneNumber.label}
            phoneCodeValue={props.contact.workPhoneCode}
            phoneNumberValue={props.contact.workPhoneNumber}
            phoneCodeFieldName={ContactFormContent.fields.workPhoneCode.field}
            phoneNumberFieldName={ContactFormContent.fields.workPhoneNumber.field}
            validationType={props.validationType}
            onPhoneCodeChange={props.onWorkPhoneCodeChange}
            onPhoneNumberChange={props.onWorkPhoneNumberChange}
            onPhoneCodeValidate={props.onWorkPhoneCodeValidate}
            onPhoneNumberValidate={props.onWorkPhoneNumberValidate}
            isPhoneCodeRequired={props.isWorkPhoneCodeRequired}
            isPhoneNumberRequired={props.isWorkPhoneNumberRequired}
            isDisabled={props.isDisabled || props.isDisabledByRegion}
            isReadOnly={props.isReadOnly}
          />
        </FieldWrapperWithChanges>
      </FieldGroup>
      <FieldGroup
        title={ContactFormContent.titles.communicationPreferences}
        isCollapsible={props.isCollapsible}
        isCollapsed={props.isCollapsed}
      >
        <div style={{ display: 'flex', gap: '16px' }}>
          <FieldWrapperWithChanges
            hasChanges={wasFieldModified(
              props.contact.modifiedValues,
              ContactFormContent.fields.isNeverCall.field
            )}
            isHidden={!props.isReadOnly}
          >
            <FormCheckBox
              defaultValue={props.contact.isNeverCall}
              field={ContactFormContent.fields.isNeverCall.field}
              label={ContactFormContent.fields.isNeverCall.label}
              validationType={props.validationType}
              formKey={props.formKeys.common}
              onChange={props.onIsNeverCallChange}
              onValidate={props.onIsNeverCallValidate}
              isRequired={props.isNeverCallRequired}
              isReadOnly={props.isReadOnly}
              isDisabled={props.isDisabled || !hasCommunicationPreferenceRole}
            />
          </FieldWrapperWithChanges>
          <FieldWrapperWithChanges
            hasChanges={wasFieldModified(
              props.contact.modifiedValues,
              ContactFormContent.fields.isNeverEmail.field
            )}
            isHidden={!props.isReadOnly}
          >
            <FormCheckBox
              defaultValue={props.contact.isNeverEmail}
              field={ContactFormContent.fields.isNeverEmail.field}
              label={ContactFormContent.fields.isNeverEmail.label}
              validationType={props.validationType}
              formKey={props.formKeys.common}
              onChange={props.onIsNeverEmailChange}
              onValidate={props.onIsNeverEmailValidate}
              isRequired={props.isNeverEmailRequired}
              isReadOnly={props.isReadOnly}
              isDisabled={props.isDisabled || !hasCommunicationPreferenceRole}
            />
          </FieldWrapperWithChanges>
        </div>
        <div className={styles.emailPixels} hidden={!isEmailPixelsInScope}>
          <FieldWrapperWithChanges
            hasChanges={wasFieldModified(
              props.contact.modifiedValues,
              ContactFormContent.fields.emailPixels.field
            )}
            isHidden={!props.isReadOnly}
          >
            <YesNoCheckboxes
              defaultValue={props.contact.emailPixels}
              field={ContactFormContent.fields.emailPixels.field}
              label={ContactFormContent.fields.emailPixels.label}
              formKey={props.formKeys.common}
              onChange={props.onEmailPixelsChange}
              onValidate={props.onEmailPixelsValidate}
              isVisible={isEmailPixelsInScope}
              isRequired={isEmailPixelsRequired}
              isReadOnly={props.isReadOnly}
              isDisabled={props.isDisabled || !hasCommunicationPreferenceRole}
            />
          </FieldWrapperWithChanges>
        </div>
      </FieldGroup>
    </>
  )
})

ContactCommonInfo.displayName = 'ContactCommonInfo'
